import { atom } from "jotai";
import { DrawerState, TabType, drawerAtom, selectedTabAtom } from "./drawer.atoms";
import { DeductionDocument } from "@/core/entities/deduction";
import { deductionAtom } from "../deduction/deduction.atoms";

// Set drawer info
export const setDrawerInfoAtom = atom(null, (get, set, drawerInfo: DrawerState["drawerInfo"]) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    drawerInfo: drawerInfo,
  });
});

// Clear drawer info
export const clearDrawerInfoAtom = atom(null, (get, set) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    drawerInfo: null,
    isDrawerModal: true,
  });
  set(selectedTabAtom, null);
});

// Set drawer open
export const setDrawerOpenAtom = atom(null, (get, set, isDrawerOpen: boolean) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isDrawerOpen: isDrawerOpen,
    isDrawerModal: true,
  });
});

// Set is first item
export const setIsFirstItemAtom = atom(null, (get, set, isFirstItem: boolean) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isFirstItem: isFirstItem,
  });
});

// Set is last item
export const setIsLastItemAtom = atom(null, (get, set, isLastItem: boolean) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isLastItem: isLastItem,
  });
});

// Set is next prev enabled
export const setIsNextPrevEnabledAtom = atom(null, (get, set, isNextPrevEnabled: boolean) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isNextPrevEnabled: isNextPrevEnabled,
  });
});

// Set viewer document
export const setViewerDocumentAtom = atom(null, (get, set, viewerDocuments: DeductionDocument[]) => {
  const supportedFileTypes = new Set([
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/jpg",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/tiff",
    "image/svg+xml",
    "image/webp",
    "text/csv",
    "text/plain",
  ]);

  const filteredViewerDocuments = viewerDocuments.filter(document => supportedFileTypes.has(document.fileMimeType));
  set(drawerAtom, {
    ...get(drawerAtom),
    viewerDocuments: filteredViewerDocuments,
  });
});

// Clear viewer document
export const clearViewerDocumentsAtom = atom(null, (get, set) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    viewerDocuments: null,
  });
});

// Set is document viewer open
export const setIsDocumentViewerOpenAtom = atom(null, (get, set, isDocumentViewerOpen: boolean) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isDocumentViewerOpen: isDocumentViewerOpen,
  });
});

// Clear is document viewer open
export const clearIsDocumentViewerOpenAtom = atom(null, (get, set) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isDocumentViewerOpen: false,
  });
});

// Close Document Viewer
export const setCloseDocumentViewerAtom = atom(null, (get, set) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isDocumentViewerOpen: false,
    viewerDocuments: null,
  });
});

// Set has any unsaved changes
export const setHasAnyUnsavedChangesAtom = atom(null, (get, set, hasAnyUnsavedChanges: boolean) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    hasAnyUnsavedChanges: hasAnyUnsavedChanges,
  });
});

// Set current expanded deduction id
export const setCurrentExpandedDeductionIdAtom = atom(null, (get, set, currentExpandedDeductionId: number) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    currentExpandedDeductionId: currentExpandedDeductionId,
  });
});

// Clear current expanded deduction id
export const clearCurrentExpandedDeductionIdAtom = atom(null, (get, set) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    currentExpandedDeductionId: null,
  });
});

// Set selected tab
export const setSelectedTabAtom = atom(null, (_, set, selectedTab: TabType | null) => {
  set(selectedTabAtom, selectedTab);
});

// Set is drawer modal
export const setIsDrawerModalAtom = atom(null, (get, set, isDrawerModal: boolean) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isDrawerModal: isDrawerModal,
  });
});

// Clear is drawer modal
export const clearIsDrawerModalAtom = atom(null, (get, set) => {
  set(drawerAtom, {
    ...get(drawerAtom),
    isDrawerModal: false,
  });
});

/** Programmatic drawer close after successful mutations (no unsaved-changes prompt). */
export const closeDeductionDrawerAtom = atom(null, (get, set) => {
  set(setDrawerOpenAtom, false);
  set(setHasAnyUnsavedChangesAtom, false);
  set(setCloseDocumentViewerAtom);
  set(clearCurrentExpandedDeductionIdAtom);
  set(clearDrawerInfoAtom);
  set(deductionAtom, {
    ...get(deductionAtom),
    editMode: null,
  });
});
